import type { Light } from '../../light';
import { SimplexNoise } from '../../util/SimplexNoise';
import type { FlickerConfigDef } from '../../composite/types';

/**
 * Modulates a Light's effective color per frame to fake a flickering candle /
 * torch flame. Driven by a composite's `emitLight` state property (0..1) — at
 * 0, the behaviour is paused and the light is dark; at 1, full base brightness
 * with the configured flicker depth.
 *
 * Color is written as `baseColor * intensity * emitLight * (1 + amplitude * noise)`.
 * Since the engine's deferred shader reads `light.color` directly (no separate
 * intensity field), we fold intensity * emitLight * flicker into color.
 */
export class LightFlickerBehaviour {
  private readonly noise?: SimplexNoise;
  private readonly seedX: number;
  private elapsedMs = 0;

  constructor(
    private readonly light: Light,
    private readonly baseColor: readonly [number, number, number],
    private readonly baseIntensity: number,
    private readonly config: FlickerConfigDef,
    /** Per-placement seed; combined with config.noiseSeed for variety. */
    placementSeed = 0,
  ) {
    const seed = (config.noiseSeed ?? 0) ^ (placementSeed | 0);
    this.seedX = (seed & 0xffff) * 0.123;
    if (config.profile === 'perlin') {
      // SimplexNoise is deterministic per (x, y); we drive y from time and
      // x from a per-instance seed so neighbouring lanterns don't sync.
      this.noise = new SimplexNoise(seed >>> 0);
    }
  }

  /**
   * Per-frame tick. `emitLight` is the composite's current state value.
   *
   * Mutates `light.color` in place to avoid per-frame array allocation (50
   * lanterns at 60 Hz is 6K allocs/sec otherwise). In-place mutation also
   * sidesteps the `Light` constructor's `specular = color` aliasing: when
   * `buildLightFromSlot` omits `specular`, the two fields share a buffer and
   * reassigning would silently desync them — mutation keeps both tracking.
   */
  tick(deltaMs: number, emitLight: number): void {
    this.elapsedMs += deltaMs;
    const c = this.light.color;
    if (emitLight <= 0) {
      // Light fully off: zero the color so the deferred shader contributes nothing.
      c[0] = 0;
      c[1] = 0;
      c[2] = 0;
      return;
    }
    const t = (this.elapsedMs * 0.001) * this.config.frequencyHz;
    const wave = this.sample(t);
    const scale = this.baseIntensity * emitLight * (1 + this.config.amplitude * wave);
    c[0] = this.baseColor[0] * scale;
    c[1] = this.baseColor[1] * scale;
    c[2] = this.baseColor[2] * scale;
  }

  /** Returns a value in roughly [-1, 1] driven by `t` (in cycles). */
  private sample(t: number): number {
    if (this.noise) {
      // sample2d returns [0, 1]; map to [-1, 1].
      return this.noise.sample2d(this.seedX, t) * 2 - 1;
    }
    // Sine profile — phase-shifted by seed so adjacent lights aren't in lockstep.
    return Math.sin((t + this.seedX) * Math.PI * 2);
  }
}
